import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { toast } from 'sonner';

export interface WhatsAppSettings {
  id?: string;
  phone_number: string | null;
  phone_verified: boolean;
  notifications_enabled: boolean;
  notify_referral_signup: boolean;
  notify_referral_subscribed: boolean;
  notify_task_overdue: boolean;
  notify_proposal_accepted: boolean;
  notify_payment: boolean;
  notify_weekly_summary: boolean;
}

const DEFAULT_SETTINGS: WhatsAppSettings = {
  phone_number: null,
  phone_verified: false,
  notifications_enabled: true,
  notify_referral_signup: true,
  notify_referral_subscribed: true,
  notify_task_overdue: true,
  notify_proposal_accepted: true,
  notify_payment: true,
  notify_weekly_summary: true,
};

export function useWhatsAppSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<WhatsAppSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_whatsapp_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSettings({
          id: data.id,
          phone_number: data.phone_number,
          phone_verified: data.phone_verified ?? false,
          notifications_enabled: data.notifications_enabled ?? true,
          notify_referral_signup: data.notify_referral_signup ?? true,
          notify_referral_subscribed: data.notify_referral_subscribed ?? true,
          notify_task_overdue: data.notify_task_overdue ?? true,
          notify_proposal_accepted: data.notify_proposal_accepted ?? true,
          notify_payment: data.notify_payment ?? true,
          notify_weekly_summary: data.notify_weekly_summary ?? true,
        });
      }
    } catch (error) {
      console.error('Error fetching WhatsApp settings:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSettings = async (newSettings: Partial<WhatsAppSettings>) => {
    if (!user) return false;

    setSaving(true);
    try {
      const updateData = {
        user_id: user.id,
        phone_number: newSettings.phone_number ?? settings.phone_number,
        phone_verified: newSettings.phone_verified ?? settings.phone_verified,
        notifications_enabled: newSettings.notifications_enabled ?? settings.notifications_enabled,
        notify_referral_signup: newSettings.notify_referral_signup ?? settings.notify_referral_signup,
        notify_referral_subscribed: newSettings.notify_referral_subscribed ?? settings.notify_referral_subscribed,
        notify_task_overdue: newSettings.notify_task_overdue ?? settings.notify_task_overdue,
        notify_proposal_accepted: newSettings.notify_proposal_accepted ?? settings.notify_proposal_accepted,
        notify_payment: newSettings.notify_payment ?? settings.notify_payment,
        notify_weekly_summary: newSettings.notify_weekly_summary ?? settings.notify_weekly_summary,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('user_whatsapp_settings')
        .upsert(updateData, { onConflict: 'user_id' });

      if (error) throw error;

      setSettings((prev) => ({ ...prev, ...newSettings }));
      toast.success('Configurações de WhatsApp salvas');
      return true;
    } catch (error: any) {
      console.error('Error updating WhatsApp settings:', error);
      toast.error('Erro ao salvar configurações');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const verifyPhone = async (phone: string) => {
    if (!user) return false;

    setSaving(true);
    try {
      // Send a test message via send-whatsapp
      const { data, error } = await supabase.functions.invoke('send-whatsapp', {
        body: {
          phone,
          message: '✅ Seu WhatsApp foi verificado com sucesso no Obrim!\n\nVocê receberá notificações importantes sobre suas obras e projetos.',
          user_id: user.id,
          message_type: 'text',
          metadata: { type: 'verification' },
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Falha ao enviar mensagem');

      // Update settings with verified phone
      await updateSettings({
        phone_number: phone,
        phone_verified: true,
      });

      toast.success('Número verificado com sucesso!');
      return true;
    } catch (error: any) {
      console.error('Error verifying phone:', error);
      toast.error(error.message || 'Erro ao verificar número');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const sendTestMessage = async () => {
    if (!user || !settings.phone_number) return false;

    try {
      const { data, error } = await supabase.functions.invoke('send-whatsapp', {
        body: {
          phone: settings.phone_number,
          message: '🧪 Esta é uma mensagem de teste do Obrim!\n\nSe você recebeu esta mensagem, suas notificações de WhatsApp estão funcionando corretamente.',
          user_id: user.id,
          message_type: 'text',
          metadata: { type: 'test' },
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Falha ao enviar mensagem');

      toast.success('Mensagem de teste enviada!');
      return true;
    } catch (error: any) {
      console.error('Error sending test message:', error);
      toast.error(error.message || 'Erro ao enviar mensagem de teste');
      return false;
    }
  };

  return {
    settings,
    loading,
    saving,
    updateSettings,
    verifyPhone,
    sendTestMessage,
    refetch: fetchSettings,
  };
}
